import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiFilter, FiEdit2, FiTrash2, FiEye, FiCheck, FiAlertCircle, FiEyeOff } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import {
  collection, getDocs, doc, updateDoc, deleteDoc, writeBatch, query, where, limit, orderBy, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import { writeAuditLog } from '../../utils/validationHelpers';

// Editable fields. `numeric` → supports inc/dec modes; `setOnly` numeric → set value only.
const FIELDS = [
  { key: 'salePrice', label: 'Sale Price', numeric: true },
  { key: 'purchasePrice', label: 'Purchase Price', numeric: true },
  { key: 'wholesalePrice', label: 'Wholesale Price', numeric: true },
  { key: 'dealerPrice', label: 'Dealer Price', numeric: true },
  { key: 'mrp', label: 'MRP', numeric: true },
  { key: 'gstRate', label: 'GST Rate (%)', numeric: true, setOnly: true },
  { key: 'taxable', label: 'Taxable', type: 'bool' },
  { key: 'unitOfMeasure', label: 'Unit of Measure', type: 'text' },
  { key: 'trackingType', label: 'Tracking Type', type: 'tracking', setOnly: true },
  { key: 'categoryId', label: 'Category', type: 'category' },
  { key: 'reorderQuantity', label: 'Reorder Qty', numeric: true, setOnly: true },
  { key: 'isActive', label: 'Status (Active)', type: 'bool' },
];

const TRACKING_TYPES = ['None', 'Batch', 'Serial'];

const MODES = [
  { key: 'set', label: 'Set to' },
  { key: 'incPct', label: 'Increase by %' },
  { key: 'decPct', label: 'Decrease by %' },
  { key: 'incAmt', label: 'Increase by amount' },
  { key: 'decAmt', label: 'Decrease by amount' },
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const BulkItemUpdate = () => {
  const { activeCompany, currentUser } = useAuth();
  const { canEdit } = usePagePermissions('masterData', 'bulkItemUpdate');
  const cid = activeCompany?.id;

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { label, done, total }

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subCategoryFilter, setSubCategoryFilter] = useState('all');
  const [trackingFilter, setTrackingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  const [selected, setSelected] = useState(() => new Set());

  const [fieldKey, setFieldKey] = useState('salePrice');
  const [mode, setMode] = useState('set');
  const [value, setValue] = useState('');
  const [subValue, setSubValue] = useState(''); // TARGET sub-category when transferring Category
  const [preview, setPreview] = useState(null);
  const [alert, setAlert] = useState(null);
  const [pendingItems, setPendingItems] = useState(null); // items skipped by delete (have transactions)

  const field = FIELDS.find(f => f.key === fieldKey) || FIELDS[0];

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [itemsSnap, catSnap, subCatSnap] = await Promise.all([
        getDocs(query(collection(db, 'companies', cid, 'inventoryItems'), orderBy('itemCode'))),
        getDocs(collection(db, 'companies', cid, 'inventoryCategories')),
        getDocs(collection(db, 'companies', cid, 'inventorySubCategories')),
      ]);
      setItems(itemsSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));
      setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isDeleted !== true));
      setSubCategories(subCatSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isDeleted !== true));
    } catch (e) {
      console.error('Error loading items:', e);
      setAlert({ type: 'error', msg: 'Failed to load items' });
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const catName = useCallback((id) => categories.find(c => c.id === id)?.categoryName || '-', [categories]);
  const subCatName = useCallback((id) => subCategories.find(s => s.id === id)?.subCategoryName || '', [subCategories]);

  // Sub-categories that belong to the chosen TARGET category (for the Category-transfer picker).
  const editSubCats = useMemo(
    () => (value ? subCategories.filter(s => s.categoryId === value) : []),
    [subCategories, value]
  );

  const filteredSubCats = useMemo(
    () => (categoryFilter === 'all' ? subCategories : subCategories.filter(s => s.categoryId === categoryFilter)),
    [subCategories, categoryFilter]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (categoryFilter !== 'all' && i.categoryId !== categoryFilter) return false;
      if (subCategoryFilter !== 'all' && i.subCategoryId !== subCategoryFilter) return false;
      if (trackingFilter !== 'all' && (i.trackingType || 'None') !== trackingFilter) return false;
      if (statusFilter === 'active' && i.isActive === false) return false;
      if (statusFilter === 'inactive' && i.isActive !== false) return false;
      if (q && !(`${i.itemName || ''} ${i.itemCode || ''} ${i.barcode || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, search, categoryFilter, subCategoryFilter, trackingFilter, statusFilter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(i => selected.has(i.id));

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
    setPreview(null);
  };
  const toggleAll = () => {
    setSelected(prev => {
      if (filtered.every(i => prev.has(i.id))) {
        const n = new Set(prev); filtered.forEach(i => n.delete(i.id)); return n;
      }
      const n = new Set(prev); filtered.forEach(i => n.add(i.id)); return n;
    });
    setPreview(null);
  };

  const selectedItems = useMemo(() => items.filter(i => selected.has(i.id)), [items, selected]);

  const computeNew = useCallback((oldVal) => {
    if (field.type === 'bool') return value === 'true';
    if (field.type === 'text') return value;
    if (field.type === 'category') return value;
    if (field.type === 'tracking') return value;
    const v = Number(value) || 0;
    const o = Number(oldVal) || 0;
    if (field.setOnly || mode === 'set') return round2(v);
    if (mode === 'incPct') return round2(o * (1 + v / 100));
    if (mode === 'decPct') return round2(o * (1 - v / 100));
    if (mode === 'incAmt') return round2(o + v);
    if (mode === 'decAmt') return round2(o - v);
    return round2(v);
  }, [field, mode, value]);

  const displayVal = (item, key) => {
    const f = FIELDS.find(x => x.key === key);
    const v = item[key];
    if (f?.type === 'bool') return v === true ? 'Yes' : (key === 'isActive' ? (v === false ? 'No' : 'Yes') : 'No');
    if (f?.type === 'category') return catName(v) + (item.subCategoryId ? ' / ' + subCatName(item.subCategoryId) : '');
    if (f?.type === 'tracking') return v || 'None';
    return v ?? (f?.numeric ? 0 : '-');
  };

  const buildPreview = async () => {
    if (!selectedItems.length) { setAlert({ type: 'error', msg: 'Select at least one item' }); return; }
    if (field.numeric && value === '') { setAlert({ type: 'error', msg: 'Enter a value' }); return; }
    if (field.type === 'category' && !value) { setAlert({ type: 'error', msg: 'Pick a category' }); return; }
    if ((field.type === 'bool' || field.type === 'tracking') && !value) { setAlert({ type: 'error', msg: 'Choose a value' }); return; }
    setBusy(true);
    try {
      // Tracking type can't change once an item has stock history (batch/serial
      // movements already exist) — flag those rows as blocked.
      let blocked = new Set();
      if (field.key === 'trackingType') {
        const checks = await Promise.all(selectedItems.map(async (it) => ({ id: it.id, has: await hasHistory(it.id) })));
        blocked = new Set(checks.filter(c => c.has).map(c => c.id));
      }
      const rows = selectedItems.map(it => ({
        id: it.id, name: it.itemName, code: it.itemCode,
        oldVal: displayVal(it, fieldKey),
        newVal: field.type === 'category' ? catName(value) + (subValue ? ' / ' + subCatName(subValue) : '')
          : field.type === 'bool' ? (computeNew() ? 'Yes' : 'No')
          : computeNew(it[fieldKey]),
        raw: computeNew(it[fieldKey]),
        blocked: blocked.has(it.id),
      }));
      setPreview(rows);
      setAlert(null);
    } catch (e) {
      console.error('Preview failed:', e);
      setAlert({ type: 'error', msg: `Preview failed: ${e.message}` });
    } finally { setBusy(false); setProgress(null); }
  };

  const applyEdit = async () => {
    if (!canEdit) { setAlert({ type: 'error', msg: 'No permission' }); return; }
    if (!preview) return;
    const applyRows = preview.filter(p => !p.blocked);
    const skipped = preview.length - applyRows.length;
    if (applyRows.length === 0) { setAlert({ type: 'error', msg: 'All selected items have history and can\'t change tracking type.' }); return; }
    if (!window.confirm(`Apply "${field.label}" to ${applyRows.length} item(s)?${skipped ? `\n\n${skipped} item(s) with history will be SKIPPED.` : ''}\n\nThis affects live data.`)) return;
    setBusy(true);
    try {
      const ids = applyRows.map(p => p.id);
      const valById = Object.fromEntries(applyRows.map(p => [p.id, p.raw]));
      setProgress({ label: `Updating ${ids.length} item(s)`, done: 0, total: ids.length });
      for (let i = 0; i < ids.length; i += 400) {
        const batch = writeBatch(db);
        ids.slice(i, i + 400).forEach(id => {
          // Category transfer moves BOTH the category AND its sub-category together,
          // so the item never keeps a sub-category that belongs to the old category.
          const patch = fieldKey === 'categoryId'
            ? { categoryId: value, subCategoryId: subValue || '', updatedAt: serverTimestamp() }
            : { [fieldKey]: valById[id], updatedAt: serverTimestamp() };
          batch.update(doc(db, 'companies', cid, 'inventoryItems', id), patch);
        });
        await batch.commit();
        setProgress({ label: `Updating ${ids.length} item(s)`, done: Math.min(i + 400, ids.length), total: ids.length });
      }
      await writeAuditLog(cid, currentUser?.uid, 'bulk-edit', 'inventoryItem', '(multiple)',
        `Set ${field.label} on ${ids.length} items (mode ${field.setOnly ? 'set' : mode}, value ${value})`);
      setAlert({ type: 'success', msg: `Updated ${field.label} on ${ids.length} item(s)${skipped ? ` · ${skipped} skipped (have history)` : ''}` });
      setPreview(null); setSelected(new Set());
      load();
    } catch (e) {
      console.error('Bulk edit failed:', e);
      setAlert({ type: 'error', msg: `Update failed: ${e.message}` });
    } finally { setBusy(false); setProgress(null); }
  };

  const hasHistory = async (itemId) => {
    const snap = await getDocs(query(collection(db, 'companies', cid, 'stockFlow'), where('itemId', '==', itemId), limit(1)));
    return !snap.empty;
  };

  const bulkDelete = async () => {
    if (!canEdit) { setAlert({ type: 'error', msg: 'No permission' }); return; }
    if (!selectedItems.length) { setAlert({ type: 'error', msg: 'Select at least one item' }); return; }
    if (!window.confirm(`Permanently delete ${selectedItems.length} item(s)?\n\nItems with NO transactions are deleted for good. Items that HAVE transactions (opening stock, purchases, sales) are skipped and listed as "pending" at the end — deleting them would break your history.`)) return;
    setBusy(true);
    setPendingItems(null);
    try {
      // 1) Check each item for transaction history — one at a time, skipping any
      //    that already have movements, so we never orphan a ledger/stock entry.
      const deletable = [], blocked = [];
      const total = selectedItems.length;
      for (let i = 0; i < selectedItems.length; i++) {
        const it = selectedItems[i];
        setProgress({ label: 'Checking for transactions', done: i, total });
        // eslint-disable-next-line no-await-in-loop
        (await hasHistory(it.id)) ? blocked.push(it) : deletable.push(it);
      }
      // 2) Permanently delete the ones with no transactions (batched, with loader).
      let deleted = 0;
      for (let i = 0; i < deletable.length; i += 400) {
        setProgress({ label: `Deleting ${deletable.length} item(s)`, done: deleted, total: Math.max(1, deletable.length) });
        const chunk = deletable.slice(i, i + 400);
        const batch = writeBatch(db);
        chunk.forEach(it => batch.delete(doc(db, 'companies', cid, 'inventoryItems', it.id)));
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
        deleted += chunk.length;
        setProgress({ label: `Deleting ${deletable.length} item(s)`, done: deleted, total: Math.max(1, deletable.length) });
      }
      if (deleted) {
        await writeAuditLog(cid, currentUser?.uid, 'bulk-delete', 'inventoryItem', '(multiple)', `Permanently deleted ${deleted} items with no transactions`);
      }
      // 3) Report: pending = items skipped because they carry transactions.
      setPendingItems(blocked);
      setAlert({
        type: deleted ? 'success' : 'error',
        msg: `Permanently deleted ${deleted} item(s).` +
          (blocked.length ? ` ${blocked.length} pending — have transactions, listed below.` : ' Nothing pending.'),
      });
      setSelected(new Set()); setPreview(null);
      load();
    } catch (e) {
      console.error('Bulk delete failed:', e);
      setAlert({ type: 'error', msg: `Delete failed: ${e.message}` });
    } finally { setBusy(false); setProgress(null); }
  };

  // Optional: deactivate the pending (has-transactions) items instead of deleting.
  // They stay in reports/history but are hidden from new transactions.
  const deactivatePending = async () => {
    if (!canEdit || !pendingItems?.length) return;
    if (!window.confirm(`Deactivate ${pendingItems.length} pending item(s)?\n\nThey keep their history and stay in reports, but won't appear when creating new invoices/purchases.`)) return;
    setBusy(true);
    try {
      const list = pendingItems;
      for (let i = 0; i < list.length; i += 400) {
        setProgress({ label: `Deactivating ${list.length} item(s)`, done: i, total: Math.max(1, list.length) });
        const batch = writeBatch(db);
        list.slice(i, i + 400).forEach(it => batch.update(doc(db, 'companies', cid, 'inventoryItems', it.id), { isActive: false, updatedAt: serverTimestamp() }));
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
      }
      await writeAuditLog(cid, currentUser?.uid, 'bulk-deactivate', 'inventoryItem', '(multiple)', `Deactivated ${list.length} pending items`);
      setAlert({ type: 'success', msg: `Deactivated ${list.length} pending item(s).` });
      setPendingItems(null);
      load();
    } catch (e) {
      console.error('Deactivate pending failed:', e);
      setAlert({ type: 'error', msg: `Deactivate failed: ${e.message}` });
    } finally { setBusy(false); setProgress(null); }
  };

  return (
    <PageLayout pageTitle="Bulk Item Update" breadcrumb="Maintain > Bulk Item Update">
      {busy && (
        <BusyOverlay>
          <BusyCard>
            <Spinner />
            <BusyLabel>{progress?.label || 'Working…'}</BusyLabel>
            {progress && progress.total > 0 && (
              <>
                <BusyCount>{progress.done} / {progress.total}</BusyCount>
                <BusyBar><BusyBarFill style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} /></BusyBar>
              </>
            )}
            <BusyHint>Please wait — don't close this page.</BusyHint>
          </BusyCard>
        </BusyOverlay>
      )}
      <Container>
        {alert && <Alert $type={alert.type}><FiAlertCircle /> {alert.msg}</Alert>}

        {/* Filters */}
        <Card>
          <Row>
            <FG><Label>Search</Label><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name / code / barcode" /></FG>
            <FG><Label>Category</Label>
              <Select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setSubCategoryFilter('all'); }}>
                <option value="all">All</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
              </Select>
            </FG>
            <FG><Label>Sub-category</Label>
              <Select value={subCategoryFilter} onChange={e => setSubCategoryFilter(e.target.value)}>
                <option value="all">All</option>
                {filteredSubCats.map(s => <option key={s.id} value={s.id}>{s.subCategoryName}</option>)}
              </Select>
            </FG>
            <FG><Label>Tracking</Label>
              <Select value={trackingFilter} onChange={e => setTrackingFilter(e.target.value)}>
                <option value="all">All</option>
                {TRACKING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </FG>
            <FG><Label>Status</Label>
              <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="all">All</option>
              </Select>
            </FG>
            <FG style={{ flex: '1 1 auto', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
              <Muted>{filtered.length} items · {selected.size} selected</Muted>
            </FG>
          </Row>
        </Card>

        {/* Bulk action bar */}
        <Card>
          <Row>
            <FG><Label>Field</Label>
              <Select value={fieldKey} onChange={e => { setFieldKey(e.target.value); setMode('set'); setValue(''); setSubValue(''); setPreview(null); }}>
                {FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </Select>
            </FG>
            {field.numeric && !field.setOnly && (
              <FG><Label>Mode</Label>
                <Select value={mode} onChange={e => { setMode(e.target.value); setPreview(null); }}>
                  {MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </Select>
              </FG>
            )}
            <FG><Label>Value</Label>
              {field.type === 'bool' ? (
                <Select value={value} onChange={e => { setValue(e.target.value); setPreview(null); }}>
                  <option value="">-- choose --</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              ) : field.type === 'category' ? (
                <>
                  <Select value={value} onChange={e => { setValue(e.target.value); setSubValue(''); setPreview(null); }}>
                    <option value="">-- to category --</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
                  </Select>
                  <Select value={subValue} disabled={!value} style={{ marginTop: 6 }}
                    onChange={e => { setSubValue(e.target.value); setPreview(null); }}>
                    <option value="">{value ? '-- to sub-category (optional) --' : '-- pick category first --'}</option>
                    {editSubCats.map(s => <option key={s.id} value={s.id}>{s.subCategoryName}</option>)}
                  </Select>
                </>
              ) : field.type === 'tracking' ? (
                <Select value={value} onChange={e => { setValue(e.target.value); setPreview(null); }}>
                  <option value="">-- choose --</option>
                  {TRACKING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              ) : (
                <Input type={field.numeric ? 'number' : 'text'} value={value} onChange={e => { setValue(e.target.value); setPreview(null); }} placeholder={field.numeric ? '0' : '...'} />
              )}
            </FG>
            <FG style={{ flex: '1 1 auto', justifyContent: 'flex-end', flexDirection: 'row', gap: 8, alignItems: 'flex-end' }}>
              <BtnSec onClick={buildPreview} disabled={busy || selected.size === 0}><FiEye /> Preview</BtnSec>
              <Btn onClick={applyEdit} disabled={busy || !preview}><FiCheck /> Apply</Btn>
              <BtnDanger onClick={bulkDelete} disabled={busy || selected.size === 0}><FiTrash2 /> Delete</BtnDanger>
            </FG>
          </Row>
        </Card>

        {/* Preview */}
        {preview && (
          <Card>
            <SubTitle><FiEdit2 /> Preview — {field.label} on {preview.length} item(s)</SubTitle>
            <TableWrap>
              <Table>
                <thead><tr><Th>Code</Th><Th>Item</Th><Th>Old</Th><Th>New</Th></tr></thead>
                <tbody>
                  {preview.slice(0, 200).map(p => (
                    <tr key={p.id} style={p.blocked ? { opacity: 0.55 } : undefined}>
                      <Td>{p.code}</Td>
                      <Td>{p.name}</Td>
                      <Td>{String(p.oldVal)}</Td>
                      <Td style={{ color: p.blocked ? colors.error.main : colors.primary.main, fontWeight: 600 }}>
                        {p.blocked ? 'skipped — has history' : String(p.newVal)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {preview.length > 200 && <Muted>+{preview.length - 200} more…</Muted>}
            </TableWrap>
          </Card>
        )}

        {/* Pending items — skipped by delete because they have transactions */}
        {pendingItems && pendingItems.length > 0 && (
          <Card>
            <SubTitle><FiAlertCircle /> Pending — {pendingItems.length} item(s) NOT deleted (have transactions)</SubTitle>
            <TableWrap>
              <Table>
                <thead><tr><Th>Code</Th><Th>Item</Th><Th>Reason</Th></tr></thead>
                <tbody>
                  {pendingItems.slice(0, 300).map(it => (
                    <tr key={it.id}>
                      <Td>{it.itemCode}</Td>
                      <Td>{it.itemName}</Td>
                      <Td style={{ color: colors.error.main }}>Has transactions — deleting would break history</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {pendingItems.length > 300 && <Muted>+{pendingItems.length - 300} more…</Muted>}
            </TableWrap>
            {canEdit && (
              <div style={{ marginTop: 10 }}>
                <BtnSec onClick={deactivatePending} disabled={busy}><FiEyeOff /> Deactivate all pending instead</BtnSec>
              </div>
            )}
          </Card>
        )}

        {/* Item grid */}
        <Card>
          <TableWrap>
            {loading ? <Empty>Loading…</Empty> : filtered.length === 0 ? <Empty>No items</Empty> : (
              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: 36 }}><input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} /></Th>
                    <Th>Code</Th><Th>Item</Th><Th>Category</Th><Th>UoM</Th>
                    <Th align="right">Purchase</Th><Th align="right">Sale</Th><Th align="right">GST%</Th><Th>Tracking</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(i => (
                    <tr key={i.id} style={{ background: selected.has(i.id) ? colors.primary.main + '0d' : 'transparent' }}>
                      <Td><input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} /></Td>
                      <Td>{i.itemCode}</Td>
                      <Td>{i.itemName}</Td>
                      <Td>{catName(i.categoryId)}</Td>
                      <Td>{i.unitOfMeasure || '-'}</Td>
                      <Td align="right">{Number(i.purchasePrice) || 0}</Td>
                      <Td align="right">{Number(i.salePrice) || 0}</Td>
                      <Td align="right">{i.taxable ? (Number(i.gstRate) || 0) : '-'}</Td>
                      <Td>{i.trackingType || 'None'}</Td>
                      <Td>{i.isActive === false ? 'Inactive' : 'Active'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableWrap>
        </Card>
      </Container>
    </PageLayout>
  );
};

// ── styled ──
const Container = styled.div` width: 100%; max-width: 1200px; margin: 0 auto; `;
const spin = keyframes` to { transform: rotate(360deg); } `;
const BusyOverlay = styled.div`
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
`;
const BusyCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg};
  padding: 28px 36px; min-width: 280px; text-align: center;
  box-shadow: ${colors.shadows.lg};
  display: flex; flex-direction: column; align-items: center; gap: 10px;
`;
const Spinner = styled.div`
  width: 38px; height: 38px; border-radius: 50%;
  border: 4px solid ${colors.neutral[200]};
  border-top-color: ${colors.primary.main};
  animation: ${spin} 0.8s linear infinite;
`;
const BusyLabel = styled.div` font-size: 15px; font-weight: 600; color: ${colors.text.primary}; `;
const BusyCount = styled.div` font-size: 13px; color: ${colors.text.secondary}; font-family: 'Consolas', monospace; `;
const BusyBar = styled.div` width: 220px; height: 8px; background: ${colors.neutral[200]}; border-radius: 5px; overflow: hidden; `;
const BusyBarFill = styled.div` height: 100%; background: ${colors.primary.main}; transition: width 0.2s; `;
const BusyHint = styled.div` font-size: 12px; color: ${colors.text.tertiary}; `;
const Card = styled.div` background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; padding: 16px; margin-bottom: 16px; `;
const Row = styled.div` display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end; `;
const FG = styled.div` display: flex; flex-direction: column; gap: 6px; flex: 0 0 190px; min-width: 0; `;
const Label = styled.label` font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; `;
const Input = styled.input` padding: 9px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; &:focus { outline: none; border-color: ${colors.primary.main}; } `;
const Select = styled.select` padding: 9px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; background: white; cursor: pointer; &:focus { outline: none; border-color: ${colors.primary.main}; } `;
const Muted = styled.span` font-size: 13px; color: ${colors.text.tertiary}; `;
const baseBtn = `display:flex;align-items:center;gap:6px;padding:9px 16px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;&:disabled{opacity:0.5;cursor:not-allowed;}`;
const Btn = styled.button` ${baseBtn} background: ${colors.primary.main}; color: white; `;
const BtnSec = styled.button` ${baseBtn} background: ${colors.neutral[100]}; color: ${colors.text.primary}; `;
const BtnDanger = styled.button` ${baseBtn} background: ${colors.error.main}; color: white; `;
const SubTitle = styled.h3` display: flex; align-items: center; gap: 8px; font-size: 15px; margin: 0 0 12px; color: ${colors.text.primary}; `;
const TableWrap = styled.div` overflow-x: auto; `;
const Table = styled.table` width: 100%; border-collapse: collapse; `;
const Th = styled.th` padding: 10px 12px; text-align: ${p => p.align || 'left'}; font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; border-bottom: 1px solid ${colors.neutral[200]}; white-space: nowrap; `;
const Td = styled.td` padding: 9px 12px; text-align: ${p => p.align || 'left'}; font-size: 13px; color: ${colors.text.primary}; border-bottom: 1px solid ${colors.neutral[100]}; `;
const Empty = styled.div` text-align: center; padding: 40px; color: ${colors.text.secondary}; `;
const Alert = styled.div` display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-radius: ${colors.borderRadius.md}; font-size: 14px; margin-bottom: 16px;
  background: ${p => p.$type === 'success' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${p => p.$type === 'success' ? colors.success.main : colors.error.main};
  border: 1px solid ${p => p.$type === 'success' ? colors.success.main + '30' : colors.error.main + '30'}; `;

export default BulkItemUpdate;
